"use client";

import { useEffect, useRef } from "react";
import * as THREE from "three";

type Shape = "icosphere" | "torusknot" | "octahedron" | "rings" | "helix";

type Props = {
  /** Size of the canvas square in px. */
  size?: number;
  /** Rotation speeds — radians per second. */
  speedX?: number;
  speedY?: number;
  /** Vertical float amplitude in scene units. */
  floatAmp?: number;
  /** Vertical float speed (lower = slower). Default 0.5. */
  floatSpeed?: number;
  /** Accent tint applied to the wireframe + lighting (defaults to #bff745). */
  accent?: string;
  /** Which 3D form to render. Default "icosphere". */
  shape?: Shape;
};

/**
 * Decorative 3D orb — a dense icosphere with subtle shading and a lime
 * wireframe overlay, drifting and rotating in place. Sits behind page
 * content as an ambient accent.
 */
export function Pentagon3D({
  size = 320,
  speedX = 0.05,
  speedY = 0.12,
  floatAmp = 0.15,
  floatSpeed = 0.5,
  accent = "#bff745",
  shape = "icosphere",
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const w = container.clientWidth;
    const h = container.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(40, w / h, 0.1, 100);
    camera.position.set(0, 0, 5.4);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "low-power",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    const accentColor = new THREE.Color(accent);

    /* ── Group everything so we can rotate/float as one ── */
    const group = new THREE.Group();
    scene.add(group);

    /* ── Standard materials shared across solid-shape variants ── */
    const coreMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color("#1a1a1a"),
      roughness: 0.85,
      metalness: 0.15,
      flatShading: true,
    });
    const wireMat = new THREE.MeshBasicMaterial({
      color: new THREE.Color("#333333"),
      wireframe: true,
      transparent: true,
      opacity: 0.6,
    });
    const edgeMat = new THREE.LineBasicMaterial({
      color: accentColor,
      transparent: true,
      opacity: 0.8,
    });
    const tubeMat = new THREE.MeshStandardMaterial({
      color: accentColor.clone().multiplyScalar(0.4),
      emissive: accentColor,
      emissiveIntensity: 0.55,
      roughness: 0.3,
      metalness: 0.4,
    });

    const disposables: Array<{ dispose: () => void }> = [
      coreMat,
      wireMat,
      edgeMat,
      tubeMat,
    ];

    /* ── Per-shape construction ── */
    if (shape === "rings") {
      // Three concentric thin tori at orthogonal axes — planetary feel.
      const radii = [1.5, 1.2, 0.9];
      const tubeR = 0.04;
      const axes: Array<[number, number, number]> = [
        [Math.PI / 2, 0, 0],
        [0, Math.PI / 2, Math.PI / 4],
        [Math.PI / 3, Math.PI / 4, 0],
      ];
      radii.forEach((r, i) => {
        const g = new THREE.TorusGeometry(r, tubeR, 12, 96);
        const m = new THREE.Mesh(g, tubeMat);
        m.rotation.set(...axes[i]);
        group.add(m);
        disposables.push(g);
      });
      // Tiny accent dots at the poles.
      const dotGeom = new THREE.SphereGeometry(0.07, 16, 16);
      const dotMat = new THREE.MeshBasicMaterial({ color: accentColor });
      [
        [1.5, 0, 0],
        [-1.5, 0, 0],
        [0, 1.2, 0],
      ].forEach(([x, y, z]) => {
        const d = new THREE.Mesh(dotGeom, dotMat);
        d.position.set(x, y, z);
        group.add(d);
      });
      disposables.push(dotGeom, dotMat);
    } else if (shape === "helix") {
      // Tube swept along a helix curve — coiled signal.
      const points: THREE.Vector3[] = [];
      const turns = 4;
      const heightSpan = 3;
      const radius = 0.85;
      const samples = 240;
      for (let i = 0; i <= samples; i++) {
        const t = i / samples;
        const angle = t * Math.PI * 2 * turns;
        points.push(
          new THREE.Vector3(
            Math.cos(angle) * radius,
            t * heightSpan - heightSpan / 2,
            Math.sin(angle) * radius
          )
        );
      }
      const curve = new THREE.CatmullRomCurve3(points);
      const tubeGeom = new THREE.TubeGeometry(curve, 240, 0.06, 12, false);
      const tube = new THREE.Mesh(tubeGeom, tubeMat);
      group.add(tube);
      disposables.push(tubeGeom);

      // Outer wireframe halo cage
      const cageGeom = new THREE.IcosahedronGeometry(1.6, 1);
      const cageEdges = new THREE.EdgesGeometry(cageGeom, 12);
      const cage = new THREE.LineSegments(cageEdges, edgeMat);
      group.add(cage);
      disposables.push(cageGeom, cageEdges);
    } else {
      // Solid shapes: icosphere / torusknot / octahedron
      let coreGeom: THREE.BufferGeometry;
      let cellGeom: THREE.BufferGeometry;
      let edgeSourceGeom: THREE.BufferGeometry;

      if (shape === "torusknot") {
        coreGeom = new THREE.TorusKnotGeometry(1.1, 0.36, 160, 24, 2, 3);
        cellGeom = new THREE.TorusKnotGeometry(1.105, 0.365, 160, 24, 2, 3);
        edgeSourceGeom = new THREE.TorusKnotGeometry(1.11, 0.37, 64, 10, 2, 3);
      } else if (shape === "octahedron") {
        coreGeom = new THREE.OctahedronGeometry(1.6, 2);
        cellGeom = new THREE.OctahedronGeometry(1.605, 2);
        edgeSourceGeom = new THREE.OctahedronGeometry(1.62, 0);
      } else {
        coreGeom = new THREE.IcosahedronGeometry(1.5, 3);
        cellGeom = new THREE.IcosahedronGeometry(1.505, 3);
        edgeSourceGeom = new THREE.IcosahedronGeometry(1.52, 1);
      }

      const core = new THREE.Mesh(coreGeom, coreMat);
      group.add(core);

      const wire = new THREE.Mesh(cellGeom, wireMat);
      group.add(wire);

      const edges = new THREE.EdgesGeometry(edgeSourceGeom, 12);
      const accentEdges = new THREE.LineSegments(edges, edgeMat);
      group.add(accentEdges);

      disposables.push(coreGeom, cellGeom, edgeSourceGeom, edges);
    }

    /* ── Lights ── */
    const ambient = new THREE.AmbientLight(0xffffff, 0.35);
    scene.add(ambient);

    const keyLight = new THREE.PointLight(accentColor, 3.4, 14, 1.0);
    keyLight.position.set(2.5, 2.5, 3);
    scene.add(keyLight);

    const fillLight = new THREE.PointLight(accentColor, 1.6, 10, 1.4);
    fillLight.position.set(-2, -1.5, 2);
    scene.add(fillLight);

    const rimLight = new THREE.DirectionalLight(0xffffff, 0.4);
    rimLight.position.set(-2, -1, 2);
    scene.add(rimLight);

    /* ── Resize ── */
    const onResize = () => {
      const nw = container.clientWidth;
      const nh = container.clientHeight;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener("resize", onResize);

    /* ── Pause when off-screen ── */
    let isVisible = true;
    const io = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting;
      },
      { threshold: 0 }
    );
    io.observe(container);

    let rafId = 0;
    const clock = new THREE.Clock();

    const tick = () => {
      const dt = clock.getDelta();
      const t = clock.elapsedTime;

      if (isVisible && !reducedMotion) {
        group.rotation.x += speedX * dt;
        group.rotation.y += speedY * dt;
        group.position.y = Math.sin(t * floatSpeed) * floatAmp;
        renderer.render(scene, camera);
      }

      rafId = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
      io.disconnect();
      disposables.forEach((d) => d.dispose());
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [speedX, speedY, floatAmp, floatSpeed, accent, shape]);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className="pointer-events-none"
      style={{ width: size, height: size }}
    />
  );
}
